import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Define the possible authentication states
export enum AuthState {
  INITIALIZING = 'initializing',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
  ERROR = 'error'
}

interface User {
  email: string;
  name: string;
  picture?: string;
  [key: string]: any;
}

interface LoginResponse {
  authorization_url: string;
}

interface AuthContextType {
  authState: AuthState;
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  logout: () => Promise<void>;
  checkAuthStatus: (bypassCache?: boolean) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Use state machine approach with explicit auth state
  const [authState, setAuthState] = useState<AuthState>(AuthState.INITIALIZING);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Auth caching to prevent excessive API calls
  const AUTH_CACHE_DURATION = 30000; // 30 seconds
  const lastAuthCheckRef = useRef<number>(0);
  const authCheckPromiseRef = useRef<Promise<boolean> | null>(null);

  // Enhanced debugging (disabled to reduce console noise)
  const logAuthInfo = (message: string, data?: any) => {
    // Disable all auth logging to reduce console noise
    // Enable only when debugging is needed: if (import.meta.env.DEV) {
    if (false) {
      if (data) {
        console.debug(`[Auth] ${message}`, data);
      } else {
        console.debug(`[Auth] ${message}`);
      }
    }
  };

  // Check authentication status on component mount with retry
  useEffect(() => {
    checkAuthStatusWithRetry();
  }, []);

  // New function with retry logic for initial auth check
  const checkAuthStatusWithRetry = async (retryCount = 0, maxRetries = 2): Promise<boolean> => {
    const result = await checkAuthStatus();
    
    // If authentication failed and we haven't reached max retries
    if (!result && retryCount < maxRetries) {
      const delay = Math.min(2000 * Math.pow(2, retryCount), 8000); // Exponential backoff, max 8s
      console.debug(`Auth check failed, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
      
      return new Promise((resolve) => {
        setTimeout(async () => {
          const retryResult = await checkAuthStatusWithRetry(retryCount + 1, maxRetries);
          resolve(retryResult);
        }, delay);
      });
    }
    
    return result;
  };

  const checkAuthStatus = async (forceCheck = false): Promise<boolean> => {
    // Check if we can use cached result
    const now = Date.now();
    const timeSinceLastCheck = now - lastAuthCheckRef.current;
    
    if (!forceCheck && 
        authState === AuthState.AUTHENTICATED && 
        timeSinceLastCheck < AUTH_CACHE_DURATION) {
      logAuthInfo(`Using cached auth result (checked ${timeSinceLastCheck}ms ago)`);
      return true;
    }

    // If there's already an auth check in progress, return that promise
    if (authCheckPromiseRef.current && !forceCheck) {
      logAuthInfo('Reusing in-progress auth check');
      return authCheckPromiseRef.current;
    }

    // Create new auth check promise
    const authCheckPromise = (async (): Promise<boolean> => {
      try {
        setIsLoading(true);
        
        // Only log when forcing a check or on initial load
        if (forceCheck || authState === AuthState.INITIALIZING) {
          console.log('Checking authentication status...');
        }
        
        const response = await fetch(`/api/v1/auth/status`, {
          method: 'GET',
          credentials: 'include', // Important: send cookies
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!response.ok) {
          console.error('Auth status check failed:', response.status, response.statusText);
          setAuthState(AuthState.UNAUTHENTICATED);
          setError(`Auth status check failed: ${response.status} ${response.statusText}`);
          setIsLoading(false);
          lastAuthCheckRef.current = now;
          return false;
        }

        const data = await response.json();
        
        // Only log detailed auth response on first load or when forced
        if (forceCheck || authState === AuthState.INITIALIZING) {
          console.log('Auth status response:', data);
        }

        // Parse response and store authentication data
        const isAuthenticated = !!data.authenticated;
        const userData = data.user || null;
        const authMethod = data.method || 'unknown';

        if (isAuthenticated && userData) {
          try {
            // Store the user email in localStorage
            localStorage.setItem('kms_user_email', JSON.stringify(userData.email));
            localStorage.setItem('kms_auth_check', 'true');
            
            // Session-based authentication - no token handling needed
            if (forceCheck || authState === AuthState.INITIALIZING) {
              console.log(`Session-based authentication successful (method: ${authMethod})`);
            }
            
            // Store complete user data for quicker UI updates
            localStorage.setItem('kms_auth_user', JSON.stringify(userData));
            
            // Set timestamp of successful login
            sessionStorage.setItem('last_successful_auth_time', Date.now().toString());
            
            // Only log on auth method change or initial load
            if (forceCheck || authState === AuthState.INITIALIZING) {
              console.log(`Authentication successful via ${authMethod}`);
            }
          } catch (e) {
            console.error('Error storing auth data in localStorage:', e);
          }
        } else {
          if (authState === AuthState.AUTHENTICATED) {
            console.warn('Auth check returned unauthenticated status');
          }
          // Clear any stored auth data
          localStorage.removeItem('kms_auth_user');
          localStorage.removeItem('kms_auth_check');
        }

        setAuthState(isAuthenticated ? AuthState.AUTHENTICATED : AuthState.UNAUTHENTICATED);
        setUser(userData);
        setError(null);
        setIsLoading(false);
        lastAuthCheckRef.current = now;

        return isAuthenticated;
      } catch (error) {
        console.error('Error checking auth status:', error);
        setAuthState(AuthState.ERROR);
        setError(error instanceof Error ? error.message : String(error));
        setIsLoading(false);
        lastAuthCheckRef.current = now;
        return false;
      }
    })();

    authCheckPromiseRef.current = authCheckPromise;
    return authCheckPromise;
  };

  const login = async () => {
    setIsLoading(true);
    setAuthState(AuthState.INITIALIZING);
    setError(null);
    
    try {
      const response = await axios.get<LoginResponse>(`/auth/google-login`);
      // Special case: for OAuth redirects we need to use window.location.href
      // This is one of the few legitimate cases where we can't use React Router
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Login error:', error);
      setError('Failed to initialize login process');
      setAuthState(AuthState.ERROR);
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    // Check if we're in an authentication flow - if so, skip logout to prevent loops
    const authFlowInProgress = sessionStorage.getItem('auth_flow_in_progress') === 'true';
    
    if (authFlowInProgress) {
      console.warn('[Auth] Skipping logout because auth flow is in progress');
      return;
    }
    
    // Check for rapid login/logout cycles - prevent logout if very recent login
    const lastAuthTime = sessionStorage.getItem('last_successful_auth_time');
    if (lastAuthTime) {
      const timeSinceAuth = Date.now() - parseInt(lastAuthTime);
      if (timeSinceAuth < 5000) { // 5 seconds
        console.warn(`[Auth] Skipping logout - too soon after login (${timeSinceAuth}ms)`);
        return;
      }
    }
    
    console.log('[Auth] Executing logout');
    setIsLoading(true);
    setAuthState(AuthState.INITIALIZING);
    
    try {
      await axios.post(`/auth/logout`, {}, { withCredentials: true });
      
      // Clear any locally stored data (non-sensitive)
      localStorage.removeItem('kms_auth_user');
      localStorage.removeItem('kms_auth_check');
      
      // Clear session storage flags
      sessionStorage.removeItem('direct_auth_succeeded');
      sessionStorage.removeItem('auth_flow_in_progress');
      sessionStorage.removeItem('last_successful_auth_time');
      sessionStorage.removeItem('post_auth_navigated');  // Clear navigation flag
      sessionStorage.removeItem('safari_auth_reloaded');
      
      // Clear authentication state
      setAuthState(AuthState.UNAUTHENTICATED);
      setUser(null);
      
      logAuthInfo('Logout successful');
      
      // Navigate to login page after successful logout
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      
      // Even if the API call fails, clear local state
      localStorage.removeItem('kms_auth_user');
      localStorage.removeItem('kms_auth_check');
      
      // Clear session storage flags
      sessionStorage.removeItem('direct_auth_succeeded');
      sessionStorage.removeItem('auth_flow_in_progress');
      sessionStorage.removeItem('last_successful_auth_time');
      sessionStorage.removeItem('post_auth_navigated');  // Clear navigation flag
      sessionStorage.removeItem('safari_auth_reloaded');
      
      setAuthState(AuthState.UNAUTHENTICATED);
      setUser(null);
      
      // Navigate to login page even if logout API fails
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  // Compute isAuthenticated from authState for backward compatibility
  const isAuthenticated = authState === AuthState.AUTHENTICATED;

  return (
    <AuthContext.Provider value={{ 
      authState,
      isAuthenticated, 
      user, 
      isLoading, 
      error, 
      login, 
      logout, 
      checkAuthStatus 
    }}>
      {children}
    </AuthContext.Provider>
  );
} 